import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
} from '@mui/material';
import { StreamingStatus, ProgressState } from '../hooks/useStreamingAnswers';
import { BackendResponse } from '../types/responses';
import { AnswerCard, AnswerCardSkeleton } from './AnswerCard';
import { colors } from '../../../shared/styles/theme';

interface ResultsSectionProps {
  isProcessing: boolean;
  results: BackendResponse | null;
  onAnswerEdit?: (answerId: string, newAnswer: string) => void;
  progress?: ProgressState;
  status?: StreamingStatus;
  statusMessage?: string;
}

/**
 * Displays the results of the Q&A processing.
 * Shows either the actual answers, loading skeletons, or nothing based on state.
 */
const ResultsSection: React.FC<ResultsSectionProps> = ({ 
  isProcessing, 
  results, 
  onAnswerEdit, 
  progress, 
  status, 
  statusMessage 
}) => {
  // Show results if they exist, regardless of isProcessing state
  if (results && results.answers && results.answers.length > 0) {
    const answers = results.answers;
    const isStreaming = isProcessing && progress && progress.total > 0;
    const progressPercent = progress?.percentage ?? (
      isStreaming && progress && progress.total > 0 
        ? Math.round((progress.processed / progress.total) * 100) 
        : 100
    );

    return (
      <Box sx={{ backgroundColor: colors.background.page }}>
        {/* Title and Description - Outside the Card */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h1" sx={{ 
            color: colors.text.primary,
            fontWeight: 700,
            mb: 1.5,
            fontSize: '2rem'
          }}>
            Get answers
          </Typography>
          
          {isStreaming ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ 
                color: colors.text.secondary,
                fontSize: '1rem',
                lineHeight: 1.5,
                mb: 1
              }}>
                {statusMessage || `Processing questions... ${progress?.processed || 0} of ${progress?.total || 0} completed`}
                {progress?.batch && progress?.totalBatches && (
                  <Typography component="span" sx={{ color: colors.text.disabled, ml: 1 }}>
                    (Batch {progress.batch} of {progress.totalBatches})
                  </Typography>
                )}
              </Typography>
              <Box sx={{ 
                width: '100%', 
                height: 8, 
                backgroundColor: colors.border.medium, 
                borderRadius: 4,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  width: `${progressPercent}%`, 
                  height: '100%', 
                  backgroundColor: colors.primaryLight,
                  borderRadius: 4,
                  transition: 'width 0.3s ease'
                }} />
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" sx={{ 
              color: colors.text.secondary,
              fontSize: '1rem',
              lineHeight: 1.5
            }}>
              OneSec has identified the following questions and security domains. You can accept, edit, download or copy the generated answers below.
            </Typography>
          )}
        </Box>

        {/* Answers Card */}
        <Card sx={{ 
          borderRadius: 2,
          backgroundColor: colors.background.card,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              {answers.map((answer) => (
                <AnswerCard key={answer.id} answer={answer} onAnswerEdit={onAnswerEdit} />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show loading state only if processing and no results yet
  if (isProcessing) {
    // Get status-based message
    const getStatusDisplayMessage = () => {
      if (statusMessage) return statusMessage;
      switch (status) {
        case 'loading':
          return 'Initializing...';
        case 'parsing':
          return 'Parsing your questions...';
        case 'processing':
          return progress && progress.total > 0 
            ? `Processing questions... ${progress.processed} of ${progress.total} completed`
            : 'Processing questions...';
        default:
          return 'OneSec is analyzing your security questionnaire and generating comprehensive answers...';
      }
    };

    const loadingProgressPercent = progress?.percentage ?? (
      progress && progress.total > 0 
        ? Math.round((progress.processed / progress.total) * 100) 
        : 0
    );

    return (
      <Box sx={{ backgroundColor: colors.background.page }}>
        {/* Title and Description - Outside the Card */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h1" sx={{ 
            color: colors.text.primary,
            fontWeight: 700,
            mb: 1.5,
            fontSize: '2rem'
          }}>
            Get answers
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: colors.text.secondary,
            fontSize: '1rem',
            lineHeight: 1.5,
            mb: 1
          }}>
            {getStatusDisplayMessage()}
          </Typography>

          {/* Progress bar */}
          {progress && progress.total > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1
              }}>
                <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                  {progress.processed} of {progress.total} questions
                </Typography>
                {progress.batch && progress.totalBatches && (
                  <Typography variant="body2" sx={{ color: colors.text.disabled }}>
                    Batch {progress.batch} of {progress.totalBatches}
                  </Typography>
                )}
              </Box>
              <Box sx={{ 
                width: '100%', 
                height: 8, 
                backgroundColor: colors.border.medium, 
                borderRadius: 4,
                overflow: 'hidden'
              }}>
                <Box sx={{ 
                  width: `${loadingProgressPercent}%`, 
                  height: '100%', 
                  backgroundColor: colors.primaryLight,
                  borderRadius: 4,
                  transition: 'width 0.3s ease'
                }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Skeleton Cards */}
        <Card sx={{ 
          borderRadius: 2,
          backgroundColor: colors.background.card,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              {[1, 2, 3, 4, 5].map((index) => (
                <AnswerCardSkeleton key={index} />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // No results and not processing
  return null;
};

export default ResultsSection;
